import { useShineClick } from '../../../hooks/useShineClick';


export default function HeroSection() {
  const handleShineClick = useShineClick<HTMLAnchorElement>();
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Luxury hotel lobby background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/generated/seq-luxury-hotel-lobby-86b2c7.jpg')`
        }}
      ></div>
      
      {/* Sophisticated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/70"></div>
      
      {/* Elegant decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-20 w-1 h-32 bg-gradient-to-b from-transparent via-amber-400/30 to-transparent max-md:hidden"></div>
        <div className="absolute top-20 right-20 w-1 h-32 bg-gradient-to-b from-transparent via-amber-400/30 to-transparent max-md:hidden"></div>
        <div className="absolute bottom-20 left-20 w-32 h-1 bg-gradient-to-r from-transparent via-amber-400/30 to-transparent max-md:hidden"></div>
        <div className="absolute bottom-20 right-20 w-32 h-1 bg-gradient-to-r from-transparent via-amber-400/30 to-transparent max-md:hidden"></div>
      </div>
      
      <div className="relative z-10 max-w-6xl px-5 md:px-8 pt-24 md:pt-0">
        {/* Premium service badge */}
        <div className="mb-8 md:mb-12 animate-fade-in">
          <div className="inline-flex items-center border border-amber-400/40 rounded-full px-5 sm:px-6 lg:px-8 py-2 md:px-8 md:py-3 backdrop-blur-sm bg-white/5">
            <div className="w-2 h-2 bg-amber-400 rounded-full mr-2 md:mr-3 animate-pulse"></div>
            <span className="text-amber-400 text-xs md:text-sm tracking-[0.2em] md:tracking-[0.4em] font-light uppercase">
              Premium Butler Service
            </span>
            <div className="w-2 h-2 bg-amber-400 rounded-full ml-2 md:ml-3 animate-pulse"></div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="mb-10 md:mb-16">
          <h1 className="mb-6 md:mb-8">
            <span 
              className="block text-2xl md:text-7xl font-light mb-3 md:mb-4 leading-tight md:leading-none tracking-wide text-white"
              style={{
                fontFamily: '"Noto Serif JP", serif',
                textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                letterSpacing: '0.02em'
              }}
            >
              本物の<span className="text-amber-400 font-normal">おもてなし</span>を、
            </span>
            <span 
              className="block text-xl md:text-6xl font-light text-white leading-tight"
              style={{
                fontFamily: '"Noto Serif JP", serif',
                textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                letterSpacing: '0.02em'
              }}
            >
              あなたに
            </span>
          </h1>
          
          {/* Elegant subtitle in script font */}
          <div className="mb-6 md:mb-8">
            <div className="w-24 md:w-32 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-4 md:mb-6"></div>
            <p 
              className="text-xl md:text-4xl text-amber-300 font-light italic tracking-wide px-5 sm:px-6 lg:px-8"
              style={{ 
                fontFamily: '"Dancing Script", cursive',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)'
              }}
            >
              "The Art of Exceptional Service"
            </p>
            <div className="w-24 md:w-32 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mt-4 md:mt-6"></div>
          </div>
        </div>
        
        {/* Refined description */}
        <div className="mb-10 md:mb-16 max-w-6xl mx-auto">
          <p 
            className="text-sm md:text-2xl text-gray-200 leading-relaxed font-light tracking-wide px-2"
            style={{ 
              fontFamily: '"Noto Sans JP", sans-serif',
              lineHeight: '1.8'
            }}
          >
            執事文化から学ぶ、心に響くサービスの極意。<br />
            一流のおもてなしとは、相手を思いやる心と、<br className="hidden md:inline" />
            完璧な技術の調和から生まれます。
          </p>
        </div>
        
        {/* Sophisticated CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-4 md:gap-8 justify-center items-center px-5 sm:px-6 lg:px-8">
          <a
            href="#about"
            onClick={handleShineClick}
            className="btn-gold group relative bg-transparent border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black px-8 py-4 md:px-12 md:py-5 font-medium text-sm md:text-lg transition-all duration-500 transform hover:scale-105 whitespace-nowrap cursor-pointer overflow-hidden w-full sm:w-auto text-center"
            style={{ 
              fontFamily: '"Noto Sans JP", sans-serif',
              letterSpacing: '0.1em'
            }}
          >
            <span className="relative z-10">About Butler Bell</span>
            <div className="absolute inset-0 bg-amber-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            <div className="absolute inset-0 border border-amber-400/50 transform rotate-45 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
          </a>
          
          <a
            href="#services"
            onClick={handleShineClick}
            className="btn-gold group relative bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white hover:text-black px-8 py-4 md:px-12 md:py-5 font-medium text-sm md:text-lg transition-all duration-500 whitespace-nowrap cursor-pointer overflow-hidden w-full sm:w-auto text-center"
            style={{ 
              fontFamily: '"Noto Sans JP", sans-serif',
              letterSpacing: '0.1em'
            }}
          >
            <span className="relative z-10">Our Services</span>
            <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right"></div>
          </a>
        </div>
      </div>
      
      {/* Refined scroll indicator */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-amber-400 max-md:hidden">
        <div className="flex flex-col items-center animate-bounce">
          <div className="text-xs tracking-[0.3em] mb-4 font-light">SCROLL</div>
          <div className="w-px h-20 bg-gradient-to-b from-amber-400 via-amber-400/50 to-transparent mb-3"></div>
          <div className="w-6 h-6 border border-amber-400 rounded-full flex items-center justify-center">
            <i className="ri-arrow-down-line text-sm"></i>
          </div>
        </div>
      </div>
      
      {/* Luxury corner ornaments */}
      <div className="absolute top-0 left-0 w-40 h-40 max-md:hidden">
        <div className="absolute top-8 left-8 w-24 h-px bg-amber-400/40"></div>
        <div className="absolute top-8 left-8 w-px h-24 bg-amber-400/40"></div>
        <div className="absolute top-6 left-6 w-2 h-2 border border-amber-400/60 rounded-full"></div>
      </div>
      <div className="absolute top-0 right-0 w-40 h-40 max-md:hidden">
        <div className="absolute top-8 right-8 w-24 h-px bg-amber-400/40"></div>
        <div className="absolute top-8 right-8 w-px h-24 bg-amber-400/40"></div>
        <div className="absolute top-6 right-6 w-2 h-2 border border-amber-400/60 rounded-full"></div>
      </div>
      <div className="absolute bottom-0 left-0 w-40 h-40 max-md:hidden">
        <div className="absolute bottom-8 left-8 w-24 h-px bg-amber-400/40"></div>
        <div className="absolute bottom-8 left-8 w-px h-24 bg-amber-400/40"></div>
        <div className="absolute bottom-6 left-6 w-2 h-2 border border-amber-400/60 rounded-full"></div>
      </div>
      <div className="absolute bottom-0 right-0 w-40 h-40 max-md:hidden">
        <div className="absolute bottom-8 right-8 w-24 h-px bg-amber-400/40"></div>
        <div className="absolute bottom-8 right-8 w-px h-24 bg-amber-400/40"></div>
        <div className="absolute bottom-6 right-6 w-2 h-2 border border-amber-400/60 rounded-full"></div>
      </div>
    </section>
  );
}
